import { useState, useEffect, useCallback, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BrowserRouter } from 'react-router-dom';

import HistoryView from './components/HistoryView';
import DeviceFrame from './components/DeviceFrame';
import LoginPage from './components/LoginPage';

import LiveCameraView from './components/LiveCameraView';
import SessionView from './components/SessionView';
import SettingsView from './components/SettingsView';
import AllTasksView from './components/AllTasksView';
import PlaygroundView from './components/PlaygroundView';
import EvolutionDetailView from './components/EvolutionDetailView';

import { useAuth } from './hooks/useAuth';
import { useGlobalEventStream } from './hooks/useGlobalEventStream';
import { useSessionStore } from './hooks/useSessionStore';
import { api } from './services/api';


// ── Toast notification types ──
const TOAST_CONFIG = {
  session_complete: { dot: 'bg-green-400', label: 'Session complete' },
  session_failed: { dot: 'bg-red-400', label: 'Session failed' },
  memory_update: { dot: 'bg-purple-400', label: 'Memory updated' },
  agent_message: { dot: 'bg-blue-400', label: 'Agent message' },
};

const MAX_TOASTS = 3;
const TOAST_DURATION = 3000;

// ── Single toast item ──
function ToastItem({ toast, onDismiss }) {
  const [progress, setProgress] = useState(100);
  const config = TOAST_CONFIG[toast.type] || TOAST_CONFIG.agent_message;

  useEffect(() => {
    const start = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, 100 - (elapsed / TOAST_DURATION) * 100);
      setProgress(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
        onDismiss(toast.id);
      }
    }, 50);
    return () => clearInterval(interval);
  }, [toast.id, onDismiss]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 50, scale: 0.95 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 50, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      className="relative flex items-center gap-2 px-3 py-2.5 rounded-xl bg-neutral-900/95 backdrop-blur-xl border border-white/10 shadow-lg min-w-[200px] max-w-[300px] overflow-hidden"
    >
      <div className={`w-2 h-2 rounded-full shrink-0 ${config.dot}`} />
      <span className="text-white/80 truncate flex-1" style={{ fontSize: '13px' }}>
        {toast.message || config.label}
      </span>
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/5">
        <div
          className="h-full bg-white/20 transition-none"
          style={{ width: `${progress}%` }}
        />
      </div>
    </motion.div>
  );
}

// ── Toast container ──
function NotificationManager({ toasts, onDismiss }) {
  return (
    <div className="fixed top-4 right-4 z-[200] flex flex-col gap-2 pointer-events-none">
      <AnimatePresence>
        {toasts.slice(0, MAX_TOASTS).map(toast => (
          <div key={toast.id} className="pointer-events-auto">
            <ToastItem toast={toast} onDismiss={onDismiss} />
          </div>
        ))}
      </AnimatePresence>
    </div>
  );
}

// Benchmark harness (#1133): when window.__benchmarkAuthBypass === true,
// skip the Firebase login gate so the e2e journey can drive /playground
// against the dev server without real credentials. Gated on
// `import.meta.env.DEV` so Vite constant-folds the entire bypass branch
// to `false` in prod — the auth gate can never be skipped in production
// regardless of what a client-side flag claims.
const benchmarkAuthBypass =
  import.meta.env.DEV &&
  typeof window !== 'undefined' &&
  window.__benchmarkAuthBypass === true;

// The playground is an admin/developer-only surface (end users use the
// native app). Access is gated on the user's `role` (managed via admin-web
// /users). This screen is shown to a signed-in non-admin, or while the role
// is still resolving.
function PlaygroundAdminGate({ state, user, onLogout }) {
  return (
    <div className="flex items-center justify-center w-full h-full bg-neutral-950 text-white/90">
      {state === 'loading' ? (
        <div className="text-white/50 text-sm">Loading…</div>
      ) : (
        <div className="max-w-sm text-center px-6">
          <div className="text-2xl mb-3">🔒</div>
          <h1 className="text-lg font-semibold mb-2">Admin access required</h1>
          <p className="text-white/60 text-sm mb-5">
            The playground is restricted to admin accounts.
            {user?.email ? <> Your account <span className="text-white/80">{user.email}</span> doesn’t have admin access.</> : null}
            {' '}Ask an admin to grant your role in the admin console.
          </p>
          {onLogout ? (
            <button
              onClick={onLogout}
              className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-sm transition-colors"
            >
              Sign out
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}

function App() {
  // Auth state (optional — not required for live mode)
  const auth = useAuth();

  // Benchmark harness: seed a synthetic viUserId on the api module so SSE
  // connections can form without a real Firebase session. Gated by the
  // __benchmarkAuthBypass flag — zero impact on normal sessions.
  // {persist:false} keeps the synthetic id in-memory only so the e2e
  // dev browser profile is not permanently polluted with a bogus user id.
  useEffect(() => {
    if (benchmarkAuthBypass && !api.getViUserId()) {
      api.setViUserId('benchmark-test-user', { persist: false });
    }
  }, []);

  // Session store — card/task state machine (replaces useManaged Agent runnerResults)
  const sessionStore = useSessionStore();

  // View state
  const [viewState, setViewState] = useState(() => {
    const path = window.location.pathname;
    if (path === '/playground' || path.startsWith('/playground/')) return 'playground';
    if (path === '/memories' || path === '/memory') return 'memory';
    if (path === '/home' || path === '/history') return 'home';
    return 'camera';
  });

  // Base page: Home(0, left) ↔ Camera(1, right). Default starts at Camera.
  const isBasePage = viewState === 'camera' || viewState === 'home';
  const basePageIndex = viewState === 'camera' ? 1 : 0;
  // Swipe state
  const swipeTouchRef = useRef(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);

  const handleSwipeTouchStart = useCallback((e) => {
    if (!isBasePage) return;
    const touch = e.touches[0];
    swipeTouchRef.current = { x: touch.clientX, y: touch.clientY, time: Date.now(), lastX: touch.clientX };
    setIsSwiping(false);
  }, [isBasePage]);

  const handleSwipeTouchMove = useCallback((e) => {
    if (!swipeTouchRef.current || !isBasePage) return;
    const touch = e.touches[0];
    const dx = touch.clientX - swipeTouchRef.current.x;
    const dy = touch.clientY - swipeTouchRef.current.y;
    // Track last position for velocity calc
    swipeTouchRef.current.lastX = touch.clientX;
    swipeTouchRef.current.lastTime = Date.now();
    // Only start horizontal swipe if clearly horizontal
    if (!isSwiping && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      setIsSwiping(true);
    }
    if (isSwiping) {
      // Rubber-band resistance at edges
      let clamped = dx;
      if (basePageIndex === 0 && dx < 0) clamped = dx * 0.15; // resist left on Home (already leftmost)
      if (basePageIndex === 1 && dx > 0) clamped = dx * 0.15; // resist right on Camera (already rightmost)
      setSwipeOffset(clamped);
    }
  }, [isBasePage, isSwiping, basePageIndex]);

  const handleSwipeTouchEnd = useCallback(() => {
    if (!swipeTouchRef.current || !isBasePage) return;
    // Calculate velocity for flick gesture
    const elapsed = Math.max(1, (Date.now() - (swipeTouchRef.current.lastTime || swipeTouchRef.current.time)));
    const velocity = Math.abs(swipeOffset) / elapsed; // px/ms
    const threshold = velocity > 0.4 ? 30 : 60; // lower threshold for fast flicks
    if (isSwiping) {
      if (basePageIndex === 0 && swipeOffset > threshold) {
        // Home → swipe right → go to Camera
        setViewState('camera');
      } else if (basePageIndex === 1 && swipeOffset < -threshold) {
        // Camera → swipe left → go to Home
        setViewState('home');
      }
    }
    swipeTouchRef.current = null;
    setSwipeOffset(0);
    setIsSwiping(false);
  }, [isBasePage, isSwiping, swipeOffset, basePageIndex]);
  const [allTasksScrollDate, setAllTasksScrollDate] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [evolutionRecord, setEvolutionRecord] = useState(null);
  // Track the sessionId returned by the backend for camera-initiated sessions
  // (LiveCameraView dispatches before navigating, SessionView needs the ID for follow-ups)
  const currentSessionIdRef = useRef(null);
  const [liveResult, setLiveResult] = useState(null);
  const [capturedPhotos, setCapturedPhotos] = useState([]);
  const [lastIntention, setLastIntention] = useState('');
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);

  // ── Badge state ──
  const [badges, setBadges] = useState({ home: 0, memory: 0 });

  // ── Memory update signal (unified from DataChannel + SSE) ──
  const [memoryUpdatedAt, setMemoryUpdatedAt] = useState(0);

  // ── Toast state ──
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);

  const addToast = useCallback((type, message) => {
    const id = ++toastIdRef.current;
    setToasts(prev => [{ id, type, message, _ts: Date.now() }, ...prev].slice(0, MAX_TOASTS));
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // ── Unified notification handler ──
  const handleNotification = useCallback((notification) => {
    if (!notification || !notification.type) return;

    if (notification.type === 'session_complete') {
      setBadges(b => ({ ...b, home: b.home + 1 }));
      addToast('session_complete', notification.message || 'Session complete');
    } else if (notification.type === 'session_failed') {
      setBadges(b => ({ ...b, home: b.home + 1 }));
      addToast('session_failed', notification.message || 'Session failed');
    } else if (notification.type === 'memory_update') {
      setBadges(b => ({ ...b, memory: b.memory + 1 }));
      setMemoryUpdatedAt(Date.now());
      addToast('memory_update', notification.preview || 'Memory updated');
    } else if (notification.type === 'agent_message') {
      addToast('agent_message', notification.message || 'New message');
    }
  }, [addToast]);

  // ── Badge reset on navigation ──
  useEffect(() => {
    if (viewState === 'home') {
      setBadges(b => b.home === 0 ? b : { ...b, home: 0 });
    } else if (viewState === 'memory') {
      setBadges(b => b.memory === 0 ? b : { ...b, memory: 0 });
    }
  }, [viewState]);

  // ── Global SSE event stream (always active — replaces useRealtimeEvents) ──
  // Only connect SSE after Firebase auth completes (avoid 401 with stale/invalid userId)
  const viUserId = (auth.isAuthenticated || benchmarkAuthBypass) ? api.getViUserId() : null;

  // Accumulate SSE events for child components (HistoryView, PlaygroundView)
  // that still consume the events array for session list updates.
  // TODO(Task 12/13): Migrate child components to callback-based pattern and remove sseEvents.
  const [sseEvents, setSseEvents] = useState([]);

  const handleSessionUpdate = useCallback((data) => {
    const event = { type: 'session_update', ...data, _ts: Date.now() };
    setSseEvents(prev => [...prev.slice(-49), event]);

    // Toast/badge notifications
    const { status, result_summary } = data;
    if (status === 'completed' || status === 'complete') {
      handleNotification({ type: 'session_complete', message: result_summary || 'Session complete' });
    } else if (status === 'failed' || status === 'error') {
      handleNotification({ type: 'session_failed', message: result_summary || 'Session failed' });
    }
  }, [handleNotification]);

  const handleMemoryUpdate = useCallback((data) => {
    const event = { type: 'memory_update', ...data, _ts: Date.now() };
    setSseEvents(prev => [...prev.slice(-49), event]);

    // Toast/badge notification (skip user-initiated updates)
    if (data.source !== 'user') {
      handleNotification({ type: 'memory_update', preview: data.preview || data.filename });
    }

    // Create a memory-update card in the session store (visible globally)
    if (data.source !== 'user' && data.preview) {
      const rnd = Math.random().toString(36).slice(2, 6);
      const cardId = `mem_${(data.filename || '').replace(/[^a-zA-Z0-9]/g, '_')}_${Date.now()}_${rnd}`;
      sessionStore.processEvent({
        op: 'create_card',
        taskId: `memory_${cardId}`,
        cardId,
        template: 'memory-update',
        data: {
          items: [{
            icon: '📝',
            label: data.filename || 'Memory',
            content: data.preview,
          }],
        },
        timestamp: new Date().toISOString(),
      });
      sessionStore.processEvent({
        op: 'finalize_card',
        taskId: `memory_${cardId}`,
        cardId,
        timestamp: new Date().toISOString(),
      });
    }
  }, [handleNotification, sessionStore]);

  const { connected: sseConnected } = useGlobalEventStream(viUserId, {
    onSessionUpdate: handleSessionUpdate,
    onMemoryUpdate: handleMemoryUpdate,
  });

  // Pending session return (for "Take More Photos" flow)
  const pendingSessionRef = useRef(null);

  // Session cache for preserving state across Home<->Session navigation
  // Key: sessionId, Value: { result, photos, intention, sessionData, blocks }
  const SESSION_CACHE_MAX = 10;
  const sessionCacheRef = useRef(new Map());

  // --- Playground camera lockdown ---
  // Intentional: playground is a desktop-only text chat mode. Camera and
  // microphone must be fully stopped to avoid browser permission prompts and
  // unnecessary resource usage.
  useEffect(() => {
    if (viewState === 'playground') {
      // Stop pre-warmed camera stream if it was never consumed by useLocalCamera
      if (window.__earlyCameraPromise) {
        window.__earlyCameraPromise.then(stream => {
          if (stream) stream.getTracks().forEach(t => t.stop());
        }).catch(() => {});
        window.__earlyCameraPromise = null;
      }
    }
  }, [viewState]);

  // D.3: Register navigation callback so agent can navigate user
  // When agent initiates a session (start_session tool), context includes:
  //   { photos: [url, ...], prompt: '...', source: 'agent' }
  const handleAgentNavigate = useCallback((page, context) => {
    const validPages = ['camera', 'history', 'home', 'live-session', 'memory', 'all-tasks'];
    if (!validPages.includes(page)) return;

    // Agent-initiated session: set up full session state before navigation
    if (page === 'live-session' && context?.source === 'agent') {
      sessionStore.reset(context?.sessionId || null);
      const photos = (context.photos || []).map(url => ({ type: 'photo', src: url, gcsUrl: url }));
      setCapturedPhotos(photos);
      setLastIntention(context.prompt || '');
      currentSessionIdRef.current = null;
      setSessionData({
        fromAgent: true,
        needsPromptConfirm: false,
      });
    }

    setViewState(page);
  }, [sessionStore]);

  // --- Handlers ---
  const handleBackToHistory = () => {
    // Cache current session state before navigating away
    const cacheKey = sessionData?.sessionId;
    if (cacheKey) {
      sessionCacheRef.current.set(cacheKey, {
        result: liveResult,
        photos: capturedPhotos,
        intention: lastIntention,
        sessionData,
        _ts: Date.now(),
      });
      // Evict oldest entries if cache exceeds max
      if (sessionCacheRef.current.size > SESSION_CACHE_MAX) {
        let oldestKey = null;
        let oldestTs = Infinity;
        for (const [key, val] of sessionCacheRef.current) {
          if ((val._ts || 0) < oldestTs) {
            oldestTs = val._ts || 0;
            oldestKey = key;
          }
        }
        if (oldestKey) sessionCacheRef.current.delete(oldestKey);
      }
    }
    // Clear active session filter — no session is being viewed on the home page
    sessionStore.setActiveSessionId(null);
    setViewState('home');
    setHistoryRefreshKey(k => k + 1);
  };

  const handleOpenCamera = () => {
    setViewState('camera');
  };

  const handleOpenHistory = () => {
    setViewState('home');
    setHistoryRefreshKey(k => k + 1);
  };

  const handleOpenAllTasks = (dateKey = null) => {
    setAllTasksScrollDate(dateKey);
    setViewState('all-tasks');
  };

  const handleSelectEvolution = (record) => {
    setEvolutionRecord(record);
    setViewState('evolution-detail');
  };

  const handleSelectSession = (useCaseData) => {
    // If HistoryView already called loadSession (historical replay), skip reset
    if (!useCaseData._sessionLoaded) {
      sessionStore.reset(useCaseData.sessionId || null);
    }
    const cacheKey = useCaseData.sessionId;
    // Restore from cache if available (preserves intermediate state including blocks)
    const cached = cacheKey ? sessionCacheRef.current.get(cacheKey) : null;
    if (cached) {
      setLiveResult(cached.result);
      setCapturedPhotos(cached.photos || []);
      setLastIntention(cached.intention || '');
      setSessionData(cached.sessionData);
    } else {
      setLiveResult(useCaseData.result || null);
      setCapturedPhotos(useCaseData.photos || []);
      setLastIntention(useCaseData.prompt || useCaseData.title || '');
      setSessionData({ ...useCaseData, fromHome: true });
    }
    setViewState('live-session');
  };

  // Called immediately when user clicks Done (result may be null — loading state)
  const handleViewResult = (result, photos, intention, sessionMeta) => {
    // If returning from a "take more photos" flow, merge photos into existing session
    if (pendingSessionRef.current) {
      const pending = pendingSessionRef.current;
      pendingSessionRef.current = null;
      const mergedPhotos = [...(pending.photos || []), ...(photos || [])];
      setLiveResult(pending.result || result);
      setCapturedPhotos(mergedPhotos);
      setLastIntention(pending.intention || intention || '');
      setSessionData(pending.sessionData || null);
      setViewState('live-session');
      return;
    }
    // Reset session store state — new session, fresh canvas
    // sessionMeta?.sessionId may be null for camera-initiated sessions (assigned later by backend)
    sessionStore.reset(sessionMeta?.sessionId || null);
    setLiveResult(result);
    setCapturedPhotos(photos || []);
    setLastIntention(intention || '');
    currentSessionIdRef.current = null;
    setSessionData(sessionMeta || null);
    setViewState('live-session');
  };

  // Clear a session from the cache (e.g., when deleted)
  const handleClearSessionCache = useCallback((sessionId) => {
    sessionCacheRef.current.delete(sessionId);
  }, []);

  // Card action handler for SessionView — delegates to session-scoped API
  const handleSessionCardAction = useCallback((actionData) => {
    const { action, cardId, ...payload } = actionData || {};
    const sid = currentSessionIdRef.current || sessionStore.activeSessionId;
    if (sid && cardId) {
      api.sendSessionCardAction(sid, cardId, action, payload).catch(e =>
        console.error('[CardAction] Failed to send:', e)
      );
    }

    // Handle open_url action locally
    if (action === 'open_url' && payload.url) {
      try {
        const parsed = new URL(payload.url);
        if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
          window.open(payload.url, '_blank', 'noopener,noreferrer');
        }
      } catch { /* ignore invalid URLs */ }
    }

    // Handle download_file action locally — fetch blob for cross-origin GCS URLs
    if (action === 'download_file' && payload.url) {
      try {
        const parsed = new URL(payload.url);
        if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
          const filename = payload.filename
            || decodeURIComponent(parsed.pathname.split('/').pop() || 'download');
          fetch(payload.url)
            .then(res => {
              if (!res.ok) throw new Error(`HTTP ${res.status}`);
              return res.blob();
            })
            .then(blob => {
              const a = document.createElement('a');
              a.href = URL.createObjectURL(blob);
              a.download = filename;
              document.body.appendChild(a);
              a.click();
              setTimeout(() => {
                URL.revokeObjectURL(a.href);
                a.remove();
              }, 100);
            })
            .catch(e => {
              console.error('[CardAction] Download failed, falling back to open:', e);
              window.open(payload.url, '_blank', 'noopener,noreferrer');
            });
        }
      } catch { /* ignore invalid URLs */ }
    }
  }, [sessionStore.activeSessionId]);

  // Called from session to take more photos and return to same session
  const handleAddPhotoToSession = useCallback(() => {
    pendingSessionRef.current = {
      result: liveResult,
      photos: capturedPhotos,
      intention: lastIntention,
      sessionData,
    };
    setViewState('camera');
  }, [liveResult, capturedPhotos, lastIntention, sessionData]);

  const handleProfileTap = () => {
    setViewState('memory');
  };

  // --- Auth gate: show login only after auth check completes and user is not authenticated ---
  // Camera mounts immediately (no auth.loading gate) for instant viewfinder.
  // Benchmark harness (#1133) bypasses the gate when __benchmarkAuthBypass is set.
  // Share pages (/playground/share/...) are public — bypass auth gate.
  const isSharePage = window.location.pathname.startsWith('/playground/share/');
  if (!auth.loading && !auth.isAuthenticated && !benchmarkAuthBypass && !isSharePage) {
    return (
      <div className="flex flex-col items-center h-full overflow-hidden bg-black">
        <DeviceFrame>
          <LoginPage
            onLoginWithGoogle={auth.loginWithGoogle}
            onTestLogin={auth.loginWithTestCode}
            error={auth.error}
            needsInviteCode={auth.needsInviteCode}
          />
        </DeviceFrame>
      </div>
    );
  }

  // --- Playground: admin-only full-page desktop layout, no DeviceFrame, no LiveKit ---
  // The playground is an admin/developer surface. Only role='admin' users may
  // enter. Public share pages (/playground/share/...) and the DEV benchmark
  // harness are exempt. Login still happens first (gate above); this is the
  // second gate that checks the resolved role.
  if (viewState === 'playground') {
    const playgroundExempt = isSharePage || benchmarkAuthBypass;
    if (!playgroundExempt && auth.loading) {
      return (
        <div className="w-screen h-screen"><PlaygroundAdminGate state="loading" /></div>
      );
    }
    if (!playgroundExempt && auth.user?.role !== 'admin') {
      return (
        <div className="w-screen h-screen">
          <PlaygroundAdminGate state="denied" user={auth.user} onLogout={auth.logout} />
        </div>
      );
    }
    return (
      <BrowserRouter>
        <PlaygroundView sessionStore={sessionStore} sseEvents={sseEvents} viUserId={viUserId} />
      </BrowserRouter>
    );
  }

  return (
    <div className="flex flex-col items-center h-full overflow-hidden bg-black">
      <DeviceFrame>
        <div className="relative w-full h-full font-sans select-none" style={{ background: '#F2F2F7' }}>

          {/* ═══ BASE LAYER: Home(left) ↔ Camera(right) swipeable pager ═══ */}
          {/* Hidden when overlay views (session, memory, etc.) are active */}
          <div
            className="absolute inset-0"
            style={{
              display: isBasePage ? 'flex' : 'none',
              width: '200%',
              transform: `translateX(calc(${basePageIndex * -50}% + ${isSwiping ? swipeOffset * 0.5 : 0}px))`,
              transition: isSwiping ? 'none' : 'transform 0.32s cubic-bezier(0.25, 1, 0.5, 1)',
              willChange: 'transform',
            }}
            onTouchStart={handleSwipeTouchStart}
            onTouchMove={handleSwipeTouchMove}
            onTouchEnd={handleSwipeTouchEnd}
          >
            {/* Page 0: Home (left) */}
            <div style={{ width: '50%', height: '100%', flexShrink: 0 }}>
              <HistoryView
                key={`home-${historyRefreshKey}`}
                isHome={true}
                onBack={handleBackToHistory}
                onOpenCamera={handleOpenCamera}
                onSelectSession={handleSelectSession}
                onSelectEvolution={handleSelectEvolution}
                onProfileTap={handleProfileTap}
                onClearSessionCache={handleClearSessionCache}
                onOpenAllTasks={handleOpenAllTasks}
                isAuthenticated={auth.isAuthenticated}
                user={auth.user}
                onNotification={handleNotification}
                sessionStore={sessionStore}
                memoryBadge={badges.memory}
                sseEvents={sseEvents}
                sseConnected={sseConnected}
              />
            </div>
            {/* Page 1: Camera (right) */}
            <div style={{ width: '50%', height: '100%', flexShrink: 0 }}>
              <LiveCameraView
                onOpenHistory={handleOpenHistory}
                onViewResult={handleViewResult}
                sessionIdRef={currentSessionIdRef}
              />
            </div>
          </div>

          {/* Page indicator dots — dot 0=Home (dark), dot 1=Camera (white) */}
          {isBasePage && (
            <div className="absolute top-0 left-1/2 -translate-x-1/2 z-[55] flex gap-1.5 safe-area-top" style={{ paddingTop: 8 }}>
              <div className="rounded-full transition-all duration-300" style={{ width: basePageIndex === 0 ? 16 : 5, height: 5, background: basePageIndex === 0 ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.15)' }} />
              <div className="rounded-full transition-all duration-300" style={{ width: basePageIndex === 1 ? 16 : 5, height: 5, background: basePageIndex === 1 ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.3)' }} />
            </div>
          )}

          {/* ═══ OVERLAY LAYER: Other views stack on top ═══ */}
          <AnimatePresence mode="wait">
            {viewState === 'live-session' && (
              <SessionView
                key="live-session"
                cards={sessionStore.cards}
                orderedCardIds={sessionStore.orderedCardIds}
                tasks={sessionStore.tasks}
                sessionId={sessionStore.activeSessionId}
                photos={capturedPhotos}
                intention={lastIntention}
                onBack={handleBackToHistory}
                onCardAction={handleSessionCardAction}
              />
            )}

            {viewState === 'evolution-detail' && (
              <EvolutionDetailView
                key={`evo-${evolutionRecord?.skillSlug}`}
                record={evolutionRecord}
                onBack={handleBackToHistory}
              />
            )}

            {viewState === 'history' && (
              <HistoryView
                key="history"
                onOpenCamera={handleOpenCamera}
                onSelectSession={handleSelectSession}
                onSelectEvolution={handleSelectEvolution}
                onProfileTap={handleProfileTap}
                onClearSessionCache={handleClearSessionCache}
                onOpenAllTasks={handleOpenAllTasks}
                isAuthenticated={auth.isAuthenticated}
                user={auth.user}
                onNotification={handleNotification}
                sessionStore={sessionStore}
                memoryBadge={badges.memory}
                sseEvents={sseEvents}
                sseConnected={sseConnected}
              />
            )}

            {viewState === 'memory' && (
              <SettingsView
                key="settings"
                onBack={handleBackToHistory}
                memoryUpdatedAt={memoryUpdatedAt}
              />
            )}
            {viewState === 'all-tasks' && (
              <AllTasksView
                key="all-tasks"
                onBack={() => setViewState('home')}
                onSelectSession={handleSelectSession}
                scrollToDateKey={allTasksScrollDate}
                isAuthenticated={auth.isAuthenticated}
              />
            )}
          </AnimatePresence>

{/* Global toast notifications */}
          <NotificationManager toasts={toasts} onDismiss={dismissToast} />
        </div>
      </DeviceFrame>
    </div>
  );
}

export default App;
